import {
  type ListSkusPagedFilters,
  type ProductValidationError,
  type ProductView,
  type SkusPage,
  type ValidateProductsResponse,
} from '@autone/openapi-core';
import { createAppQueryBuilder } from '@autone/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { customBaseQuery, encodeUrl } from '../../utils';

import {
  type GetCoreActiveCatalogueProductsCountRequest,
  type GetCoreActiveCatalogueProductsCountResponse,
  type GetCoreActiveLocationsCountRequest,
  type GetCoreActiveLocationsCountResponse,
  type GetCoreActiveProductsCountRequest,
  type GetCoreActiveProductsCountResponse,
  type GetCoreCatalogueEventsRequest,
  type GetCoreCatalogueEventsResponse,
  type GetCoreCatalogueSeasonsEventsRequest,
  type GetCoreCatalogueSeasonsEventsResponse,
  type GetCoreCatalogueSeasonsRequest,
  type GetCoreCatalogueSeasonsResponse,
  type GetCoreDocumentationJWTRequest,
  type GetCoreDocumentationJWTResponse,
  type GetCoreLocationClustersRequest,
  type GetCoreLocationClustersResponse,
  type GetCoreLocationCountriesRequest,
  type GetCoreLocationCountriesResponse,
  type GetCoreLocationRegionsReponse,
  type GetCoreLocationRegionsRequest,
  type GetCoreLocationsRequest,
  type GetCoreLocationsResponse,
  type GetCoreLocationTerritoriesRequest,
  type GetCoreLocationTerritoriesResponse,
  type GetCoreLocationTypesRequest,
  type GetCoreLocationTypesResponse,
  type GetCoreProductArticlesRequest,
  type GetCoreProductArticlesResponse,
  type GetCoreProductBrandsRequest,
  type GetCoreProductBrandsResponse,
  type GetCoreProductBudgetLevelsRequest,
  type GetCoreProductBudgetLevelsResponse,
  type GetCoreProductClassesRequest,
  type GetCoreProductClassesResponse,
  type GetCoreProductCollectionTypesRequest,
  type GetCoreProductCollectionTypesResponse,
  type GetCoreProductDepartmentsRequest,
  type GetCoreProductDepartmentsResponse,
  type GetCoreProductEventsRequest,
  type GetCoreProductEventsResponse,
  type GetCoreProductGroupsRequest,
  type GetCoreProductGroupsResponse,
  type GetCoreProductManufacturersRequest,
  type GetCoreProductManufacturersResponse,
  type GetCoreProductSeasonsEventsRequest,
  type GetCoreProductSeasonsEventsResponse,
  type GetCoreProductSeasonsRequest,
  type GetCoreProductSeasonsResponse,
  type GetCoreProductsRequest,
  type GetCoreProductsResponse,
  type GetCoreProductSubClassesRequest,
  type GetCoreProductSubClassesResponse,
  type GetCoreProductSubDepartmentsRequest,
  type GetCoreProductSubDepartmentsResponse,
} from './core-types';

const CORE_API_PREFIX = '/core';

const coreQueryBuilder = createAppQueryBuilder<
  'core',
  typeof CORE_API_PREFIX
>();

// Define a service using the base core URL and expected endpoints
export const coreApi = createApi({
  reducerPath: 'coreApi',
  baseQuery: async (args, api, extraOptions) => {
    // custom query adds auth headers and error handling
    return customBaseQuery(args, api, extraOptions, `v2${CORE_API_PREFIX}`);
  },
  endpoints: (builder) => ({
    getProductPeekView: builder.query<ProductView, { productId: string }>({
      query: ({ productId }) =>
        encodeUrl({
          url: `/products/{productId}/peak`,
          variables: { productId },
        }),
      transformResponse: (response: { product_view: ProductView }) =>
        response.product_view,
    }),
    getCoreSkusPaged: builder.query<
      SkusPage,
      {
        sortBy: string;
        cursor: string | null;
        limit: number;
        filters: ListSkusPagedFilters;
      }
    >({
      query: ({
        sortBy,
        cursor,
        limit,
        filters,
      }: {
        sortBy: string;
        cursor: string | null;
        limit: number;
        filters: ListSkusPagedFilters;
      }) => ({
        url: encodeUrl({
          url: `/skus?limit={limit}&sort_by={sortBy}${
            cursor ? `&cursor={cursor}` : ``
          }`,
          variables: {
            sortBy,
            limit: `${limit}`,
            cursor: cursor || '',
          },
        }),
        method: 'POST',
        body: {
          filters,
        },
      }),
    }),

    validateProductIds: builder.query<
      ProductValidationError[],
      { productIds: string[] }
    >({
      query: ({ productIds }) => ({
        url: '/products/validate',
        method: 'POST',
        body: { product_ids: productIds },
      }),
      transformResponse: (response: ValidateProductsResponse) =>
        response.validation_errors,
    }),

    /**
     * ========== LOCATION ENDPOINTS ==========
     */
    getCoreLocations: builder.query<
      GetCoreLocationsResponse,
      GetCoreLocationsRequest
    >({
      query: coreQueryBuilder('post', '/locations'),
    }),
    getCoreLocationTypes: builder.query<
      GetCoreLocationTypesResponse,
      GetCoreLocationTypesRequest
    >({
      query: coreQueryBuilder('post', '/locations/location-types'),
    }),
    getCoreLocationRegions: builder.query<
      GetCoreLocationRegionsReponse,
      GetCoreLocationRegionsRequest
    >({
      query: coreQueryBuilder('post', '/locations/regions'),
    }),
    getCoreLocationCountries: builder.query<
      GetCoreLocationCountriesResponse,
      GetCoreLocationCountriesRequest
    >({
      query: coreQueryBuilder('post', '/locations/countries'),
    }),
    getCoreLocationTerritories: builder.query<
      GetCoreLocationTerritoriesResponse,
      GetCoreLocationTerritoriesRequest
    >({
      query: coreQueryBuilder('post', '/locations/territories'),
    }),
    getCoreActiveLocationsCount: builder.query<
      GetCoreActiveLocationsCountResponse,
      GetCoreActiveLocationsCountRequest
    >({
      query: coreQueryBuilder('post', '/locations/count'),
    }),

    /**
     * ========== PRODUCT ENDPOINTS ==========
     */
    getCoreProducts: builder.query<
      GetCoreProductsResponse,
      GetCoreProductsRequest
    >({
      query: coreQueryBuilder('post', '/products'),
    }),
    getCoreProductDepartments: builder.query<
      GetCoreProductDepartmentsResponse,
      GetCoreProductDepartmentsRequest
    >({
      query: coreQueryBuilder('post', '/products/departments'),
    }),
    getCoreProductSubDepartments: builder.query<
      GetCoreProductSubDepartmentsResponse,
      GetCoreProductSubDepartmentsRequest
    >({
      query: coreQueryBuilder('post', '/products/sub-departments'),
    }),
    getCoreProductCollectionTypes: builder.query<
      GetCoreProductCollectionTypesResponse,
      GetCoreProductCollectionTypesRequest
    >({
      query: coreQueryBuilder('post', '/products/collection-types'),
    }),
    getCoreProductSeasons: builder.query<
      GetCoreProductSeasonsResponse,
      GetCoreProductSeasonsRequest
    >({
      query: coreQueryBuilder('post', '/products/seasons'),
    }),
    getCoreProductEvents: builder.query<
      GetCoreProductEventsResponse,
      GetCoreProductEventsRequest
    >({
      query: coreQueryBuilder('post', '/products/events'),
    }),
    getCoreProductBrands: builder.query<
      GetCoreProductBrandsResponse,
      GetCoreProductBrandsRequest
    >({
      query: coreQueryBuilder('post', '/products/brands'),
    }),
    getCoreProductSeasonsEvents: builder.query<
      GetCoreProductSeasonsEventsResponse,
      GetCoreProductSeasonsEventsRequest
    >({
      query: coreQueryBuilder('post', '/products/season-events'),
    }),
    getCoreProductBudgetLevels: builder.query<
      GetCoreProductBudgetLevelsResponse,
      GetCoreProductBudgetLevelsRequest
    >({
      query: coreQueryBuilder('post', '/products/budget-levels'),
    }),
    getCoreProductManufacturers: builder.query<
      GetCoreProductManufacturersResponse,
      GetCoreProductManufacturersRequest
    >({
      query: coreQueryBuilder('post', '/products/manufacturers'),
    }),
    getCoreProductClasses: builder.query<
      GetCoreProductClassesResponse,
      GetCoreProductClassesRequest
    >({
      query: coreQueryBuilder('post', '/products/classes'),
    }),
    getCoreProductSubClasses: builder.query<
      GetCoreProductSubClassesResponse,
      GetCoreProductSubClassesRequest
    >({
      query: coreQueryBuilder('post', '/products/sub-classes'),
    }),
    getCoreProductArticles: builder.query<
      GetCoreProductArticlesResponse,
      GetCoreProductArticlesRequest
    >({
      query: coreQueryBuilder('post', '/products/articles'),
    }),
    getCoreActiveProductsCount: builder.query<
      GetCoreActiveProductsCountResponse,
      GetCoreActiveProductsCountRequest
    >({
      query: coreQueryBuilder('post', '/products/count'),
    }),
    getCoreActiveCatalogueProductsCount: builder.query<
      GetCoreActiveCatalogueProductsCountResponse,
      GetCoreActiveCatalogueProductsCountRequest
    >({
      query: coreQueryBuilder('post', '/catalogue/count'),
    }),

    /**
     * ========== CATALOGUE ENDPOINTS ==========
     */
    getCoreCatalogueSeasons: builder.query<
      GetCoreCatalogueSeasonsResponse,
      GetCoreCatalogueSeasonsRequest
    >({
      query: coreQueryBuilder('post', '/catalogue/seasons'),
    }),
    getCoreCatalogueEvents: builder.query<
      GetCoreCatalogueEventsResponse,
      GetCoreCatalogueEventsRequest
    >({
      query: coreQueryBuilder('post', '/catalogue/events'),
    }),
    getCoreCatalogueSeasonsEvents: builder.query<
      GetCoreCatalogueSeasonsEventsResponse,
      GetCoreCatalogueSeasonsEventsRequest
    >({
      query: coreQueryBuilder('post', '/catalogue/season-events'),
    }),

    /**
     * ========== PRODUCT GROUPS ENDPOINTS ==========
     */
    getCoreProductGroups: builder.query<
      GetCoreProductGroupsResponse,
      GetCoreProductGroupsRequest
    >({
      query: coreQueryBuilder('post', '/product-groups'),
    }),

    /**
     * ========== LOCATION CLUSTERS ENDPOINTS ==========
     */
    getCoreLocationClusters: builder.query<
      GetCoreLocationClustersResponse,
      GetCoreLocationClustersRequest
    >({
      query: coreQueryBuilder('post', '/location-clusters'),
    }),

    /**
     * ========== AUTH ENDPOINTS ==========
     */
    getCoreDocumentationRedirectUrl: builder.query<
      GetCoreDocumentationJWTResponse,
      GetCoreDocumentationJWTRequest
    >({
      query: coreQueryBuilder('get', '/auth/documentation-jwt'),
    }),
  }),
});

export const {
  useGetCoreActiveLocationsCountQuery,
  useGetCoreActiveProductsCountQuery,
  useGetCoreActiveCatalogueProductsCountQuery,
  useGetCoreCatalogueEventsQuery,
  useGetCoreCatalogueSeasonsEventsQuery,
  useGetCoreCatalogueSeasonsQuery,
  useGetCoreProductBrandsQuery,
  useGetCoreProductClassesQuery,
  useGetCoreProductSubClassesQuery,
  useGetCoreProductDepartmentsQuery,
  useGetCoreProductSubDepartmentsQuery,
  useGetCoreProductCollectionTypesQuery,
  useGetCoreProductSeasonsQuery,
  useGetCoreProductEventsQuery,
  useGetCoreProductBudgetLevelsQuery,
  useGetCoreProductManufacturersQuery,
  useGetCoreProductSeasonsEventsQuery,
  useGetCoreProductArticlesQuery,
  useGetCoreProductsQuery,
  useGetCoreSkusPagedQuery,
  useValidateProductIdsQuery,
  useLazyValidateProductIdsQuery,
  useGetCoreLocationsQuery,
  useLazyGetCoreLocationsQuery,
  useGetCoreLocationTypesQuery,
  useGetCoreLocationRegionsQuery,
  useGetCoreLocationCountriesQuery,
  useGetCoreLocationTerritoriesQuery,
  useGetCoreLocationClustersQuery,
  useGetProductPeekViewQuery,
  useGetCoreDocumentationRedirectUrlQuery,
  useLazyGetCoreDocumentationRedirectUrlQuery,
  useGetCoreProductGroupsQuery,
} = coreApi;
